
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PaymentStatus } from "./types";

type UsePaymentProcessProps = {
  planName: string;
  planPrice: number;
  currency: string;
  planId?: string;
  returnUrl?: string;
  onOpenChange: (open: boolean) => void;
};

export const usePaymentProcess = ({
  planName,
  planPrice,
  currency,
  planId,
  returnUrl,
  onOpenChange
}: UsePaymentProcessProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [mpesaStatus, setMpesaStatus] = useState<PaymentStatus>(null);
  const [checkoutRequestID, setCheckoutRequestID] = useState<string | null>(null);
  const [statusCheckCount, setStatusCheckCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string>("");
  const [userCountry, setUserCountry] = useState<string>("Kenya");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (data.user) {
          setUserId(data.user.id);
          
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('whatsapp_number, country')
            .eq('id', data.user.id)
            .single();
            
          if (!profileError && profileData) {
            const phoneNumber = profileData.whatsapp_number || "";
            const cleanedNumber = phoneNumber.replace(/\D/g, "");
            
            if (cleanedNumber) {
              setUserPhone(cleanedNumber);
            }
            
            if (profileData.country) {
              setUserCountry(profileData.country);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    
    fetchUser();
  }, []);

  useEffect(() => {
    let statusInterval: ReturnType<typeof setInterval>;
    
    if (checkoutRequestID && mpesaStatus === "initiated" && statusCheckCount < 10) {
      statusInterval = setInterval(() => {
        checkMpesaStatus(checkoutRequestID);
        setStatusCheckCount(prev => prev + 1);
      }, 5000);
    }
    
    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    };
  }, [checkoutRequestID, mpesaStatus, statusCheckCount]);

  const checkMpesaStatus = useCallback(async (requestID: string) => {
    try {
      console.log("Checking M-Pesa transaction status...");
      
      const { data, error } = await supabase.functions.invoke("mpesa-payment", {
        body: {
          action: "checkStatus",
          checkoutRequestID: requestID,
          userId: userId
        }
      });
      
      if (error) {
        console.error("Error checking M-Pesa status:", error);
        return;
      }
      
      console.log("M-Pesa status check result:", data);
      
      if (data?.status === "completed") {
        setMpesaStatus("completed");
        toast({
          title: "Payment successful!",
          description: "Your M-Pesa payment has been completed.",
        });
        
        setTimeout(() => {
          onOpenChange(false);
          
          setTimeout(() => {
            const cacheParam = `t=${new Date().getTime()}`;
            const redirectUrl = returnUrl || "/dashboard/settings/subscription?success=true";
            const separator = redirectUrl.includes('?') ? '&' : '?';
            window.location.href = `${redirectUrl}${separator}${cacheParam}`;
          }, 1000);
        }, 2500);
      } else if (data?.status === "failed" || data?.status === "cancelled") {
        setMpesaStatus("failed");
        toast({
          title: "Payment not completed",
          description: data.message || "Your M-Pesa payment was not completed",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error in status check:", error);
    }
  }, [userId, toast, onOpenChange, returnUrl]);

  const handleSelectPaymentMethod = async (methodId: string) => {
    setSelectedMethod(methodId);
    
    if (methodId !== "mpesa") {
      processPayment(methodId);
    }
  };

  const handleMpesaSubmit = async (phoneNumber: string) => {
    try {
      await processPayment("mpesa", phoneNumber);
    } catch (error) {
      console.error("M-Pesa submission error:", error);
      toast({
        title: "Payment Error",
        description: error instanceof Error ? error.message : "Failed to process M-Pesa payment",
        variant: "destructive"
      });
      setMpesaStatus("failed");
    }
  };

  const handleContinueWithPendingPayment = () => {
    onOpenChange(false);
    const redirectUrl = returnUrl || "/dashboard/settings/subscription?mpesa=pending";
    const separator = redirectUrl.includes('?') ? '&' : '?';
    window.location.href = `${redirectUrl}${separator}t=${new Date().getTime()}`;
  };

  const handleResetSelection = () => {
    setSelectedMethod(null);
    setMpesaStatus(null);
    setCheckoutRequestID(null);
  };

  const processPayment = async (methodId: string, phoneNumber?: string) => {
    try {
      setIsProcessing(true);
      console.log(`Processing payment via ${methodId} for ${planName} plan`);
      
      toast({
        title: "Payment processing",
        description: `Processing payment via ${methodId} for the ${planName} plan`,
      });
      
      if (methodId === "card") {
        const { data: userData } = await supabase.auth.getUser();
        const customerId = userData.user?.email || "";
        
        const { data, error } = await supabase.functions.invoke("stripe-payment", {
          body: {
            planName,
            planPrice,
            currency,
            customerId,
            userId,
            planId,
            returnUrl
          }
        });
        
        if (error) {
          throw new Error(`Error processing payment: ${error.message}`);
        }
        
        if (data?.url) {
          const urlObj = new URL(data.url);
          urlObj.searchParams.append('t', Date.now().toString());
          window.location.href = urlObj.toString();
          return;
        } else {
          throw new Error("No checkout URL returned from payment processor");
        }
      } else if (methodId === "mpesa") {
        if (!phoneNumber) {
          throw new Error("Phone number is required for M-Pesa payment");
        }
        
        setMpesaStatus("pending");
        
        const { data, error } = await supabase.functions.invoke("mpesa-payment", {
          body: {
            planName,
            planPrice,
            currency,
            phoneNumber,
            userId,
            planId,
            returnUrl
          }
        });
        
        if (error) {
          console.error("M-Pesa invoke error:", error);
          setMpesaStatus("failed");
          throw new Error(`Error processing M-Pesa payment: ${error.message}`);
        }
        
        if (!data) {
          setMpesaStatus("failed");
          throw new Error("No response data from M-Pesa payment function");
        }
        
        if (data.success) {
          setMpesaStatus("initiated");
          setCheckoutRequestID(data.checkoutRequestID);
          setStatusCheckCount(0);
          
          toast({
            title: "M-Pesa payment initiated",
            description: "Please check your phone to complete the payment",
          });
        } else {
          setMpesaStatus("failed");
          throw new Error(data.message || "Failed to initiate M-Pesa payment");
        }
      } else if (methodId === "chapa") {
        const { data: userData } = await supabase.auth.getUser();
        const customerId = userData.user?.email || "";
        
        const { data, error } = await supabase.functions.invoke("chapa-payment", {
          body: {
            planName,
            planPrice,
            currency: userCountry === "Ethiopia" ? "Birr" : currency,
            customerId,
            userId,
            planId,
            returnUrl
          }
        });
        
        if (error) {
          throw new Error(`Error processing Chapa payment: ${error.message}`);
        }
        
        if (data?.url) {
          const urlObj = new URL(data.url);
          urlObj.searchParams.append('t', Date.now().toString());
          window.location.href = urlObj.toString();
          return;
        } else {
          throw new Error("No checkout URL returned from Chapa payment processor");
        }
      } else {
        onOpenChange(false);
        
        const cacheParam = `t=${new Date().getTime()}`;
        const redirectUrl = returnUrl || "/dashboard/settings/subscription";
        const separator = redirectUrl.includes('?') ? '&' : '?';
        window.location.href = `${redirectUrl}${separator}${cacheParam}`;
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      toast({
        title: "Payment Error",
        description: error instanceof Error ? error.message : "Failed to process payment. Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    selectedMethod,
    mpesaStatus,
    statusCheckCount,
    userPhone,
    userCountry,
    handleSelectPaymentMethod,
    handleMpesaSubmit,
    handleContinueWithPendingPayment,
    handleResetSelection
  };
};
